// The headline KPI tile row + the tile-as-nav behaviour: clicking a tile opens
// that metric's full-width detail section, and exactly one section is always
// expanded (default cost_artifact, set in main.ts).
import { state, $, esc, usd, num, get, fmtVal, kpiDelta, cxLabelList, scopeQs, dayOf } from './core'
import { syncHash } from './router'
import { clearAsked } from './askbanner'
import { successDefinable } from './notice'
import { renderSuccessRate } from './metrics/successRate'
import { renderCostArtifact } from './metrics/costArtifact'
import { renderTotalSpend } from './metrics/totalSpend'
import { renderChurn } from './metrics/churn'
import { renderOps } from './metrics/ops'
import { renderDefectRate, loadDefectRate, getDrKpi } from './metrics/defectRate'
import { renderTimeToShip, loadTimeToShip, getTtsKpi, dur } from './metrics/timeToShip'
import { smartDefaultKind, resolveKind } from './caKind'

// One headline tile. `valueHtml` opts the value out of escaping (it carries
// pre-built safe markup, e.g. the churn composite); `valueClass` adds a style
// hook. Both optional so plain scalar tiles stay terse.
interface Tile {
  label: string;
  value: string;
  delta: string;
  sub: string;
  metric?: string;
  valueHtml?: boolean;
  valueClass?: string;
  /** Hover disclosure, for a tile whose full context doesn't fit as sub text. */
  title?: string;
}

// The top-level window selector + caption. Sets the window every headline tile
// AND every expansion's charts are computed over. Lives where the caption used
// to, so "Last N days" is now an adjustable control, not just a label.
var WINDOWS = [['7', '7d'], ['14', '14d'], ['30', '30d'], ['90', '90d'], ['all', 'All']];
export function renderWindow() {
  var cap = $('#kpi-caption');
  if (!cap) return;
  // Churn can't use the day selector (a 7-day window vs the maturity gate is
  // empty), so when its detail is open the top row shows churn's OWN time
  // controls instead — the Cohorts span + the selectable window T. Everything
  // else keeps the shared 7/14/30/90 selector.
  if (state.metric === 'churn') { renderChurnWindow(cap); return; }
  var btns = WINDOWS.map(function (o) {
    return '<button class="' + (String(state.days) === o[0] ? 'on' : '') + '" data-d="' + o[0] + '">' + o[1] + '</button>';
  }).join('');
  var caption = state.days === 'all'
    ? 'All time'
    : 'Last ' + state.days + ' days &middot; ▲▼ vs. previous ' + state.days + ' days';
  cap.innerHTML = '<span class="fb-grp"><span class="fb-lbl">Time</span>' +
    '<span class="win-seg seg flt-seg" id="win-seg">' + btns + '</span></span>' +
    '<span class="win-cap">' + caption + '</span>';
  Array.prototype.forEach.call($('#win-seg').children, function (btn) {
    btn.onclick = function () {
      var d = btn.getAttribute('data-d');
      state.days = d === 'all' ? 'all' : parseInt(d, 10);
      renderWindow();
      loadKpis();
      // Every expansion's charts track the same window. Re-default each curve
      // bucket for the new span (clear manual picks), then re-render whichever
      // detail is open so its charts + labels follow N.
      // (churn is absent on purpose: its bucket is not window-derived — the
      // metric ignores the selector entirely, see loadChurnKpi.)
      state.sr.bucket = ''; state.ca.bucket = ''; state.spend.bucket = ''; state.ops.bucket = ''; state.dr.bucket = '';
      renderOpenMetric();
    };
  });
}

// Churn's time controls, rendered into the top selector row in place of the day
// selector: Cohorts (span of matured history) + Window (the T-day churn cutoff).
// Changing either re-fetches the tile AND the open detail so both stay in step.
var CH_RANGES = [['56', '8w'], ['84', '12w'], ['182', '26w'], ['all', 'all']];
function renderChurnWindow(cap) {
  var ch = state.churn;
  var choices = (lastChurn && lastChurn.windowChoices) || [7, 15, 30, 60];
  var curW = ch.windowDays || (lastChurn && lastChurn.windowDays) || 30;
  var rangeBtns = CH_RANGES.map(function (r) {
    return '<button class="' + (r[0] === ch.range ? 'on' : '') + '" data-r="' + r[0] + '">' + r[1] + '</button>';
  }).join('');
  var winBtns = choices.map(function (d) {
    return '<button class="' + (String(curW) === String(d) ? 'on' : '') + '" data-w="' + d + '">' + d + 'd</button>';
  }).join('');
  cap.innerHTML =
    '<span class="fb-grp"><span class="fb-lbl" title="How far back the charts reach. Churn is a trailing figure — only cohorts old enough for their full window to have elapsed count, so the day selector above does not apply here.">Cohorts</span>' +
    '<span class="win-seg seg flt-seg" id="ch-range-top">' + rangeBtns + '</span></span>' +
    '<span class="fb-grp"><span class="fb-lbl" title="Churn window T: a line removed within this many days of merging is churn; later is normal evolution. Also sets how long a cohort must mature before it counts.">Window</span>' +
    '<span class="win-seg seg flt-seg" id="ch-win-top">' + winBtns + '</span></span>';
  Array.prototype.forEach.call($('#ch-range-top').children, function (btn) {
    btn.onclick = function () { state.churn.range = btn.getAttribute('data-r'); renderWindow(); renderOpenMetric(); };
  });
  Array.prototype.forEach.call($('#ch-win-top').children, function (btn) {
    btn.onclick = function () {
      state.churn.windowDays = btn.getAttribute('data-w');
      // T drives the tile too (its "within Nd" label + the rate), so drop the
      // cached tile payload and re-fetch alongside the detail.
      lastChurn = null; lastChurnScope = null;
      renderWindow(); loadChurnKpi(); renderOpenMetric();
    };
  });
}

// Re-render the currently expanded metric's detail (shared by openMetric and the
// window selector). No-op when no detail is open.
export function renderOpenMetric() {
  var m = state.metric;
  if (m === 'success_rate') renderSuccessRate();
  else if (m === 'cost_artifact') renderCostArtifact();
  else if (m === 'total_spend') renderTotalSpend();
  else if (m === 'time_to_ship') renderTimeToShip();
  else if (m === 'churn') renderChurn();
  else if (m === 'defect_rate') renderDefectRate();
  else if (m === 'ops') renderOps();
}

// The most recent /api/kpis payload, kept so the tile row can repaint without a
// refetch when the overview lands (which decides whether any outcomes exist at
// all — see paintKpis' Session Outcome Rate handling).
var lastKpis: any = null;

// The most recent /api/churn payload + the scope it was fetched under. Churn is
// the one tile that does NOT follow the 7d/14d/30d/90d selector (a cohort only
// counts once its churn window has fully elapsed, so the figure is a fixed
// trailing one — docs/plans/churn-rate.md; don't "fix" it to follow the
// selector). Scope changes still apply, hence the scope-keyed cache.
var lastChurn: any = null;
var lastChurnScope: string | null = null;
function loadChurnKpi() {
  // The tile shares the selected churn window T (so its "within Nd" number
  // matches the detail); scope + T together key the cache.
  var scope = scopeQs();
  var winQs = state.churn.windowDays ? '&window=' + encodeURIComponent(state.churn.windowDays) : '';
  var key = scope + winQs;
  if (lastChurn && lastChurnScope === key) return;
  // kpi=1: the tile reads only the KPI block, so the route skips the four
  // detail reads. This fetch fires on every dashboard load — without the flag
  // it would sweep the whole ledger and usage table to paint one number.
  get('/api/churn?bucket=month&kpi=1' + winQs + scope).then(function (c) {
    if (!c || c.error) return;
    lastChurn = c;
    lastChurnScope = key;
    paintKpis();
    // The top selector row shows T's choices from this payload once churn is open.
    if (state.metric === 'churn') renderWindow();
  });
}

// The complexity-filtered cost-per-artifact figure, published by the Cost-by-
// Artifact detail (renderBurn) so the headline tile can mirror the active filter.
// Tagged with its kind/window/complexity so a stale filter never paints.
var caKpiOverride: any = null;
export function setCaKpiOverride(o: any) { caKpiOverride = o; }

export function loadKpis() {
  // The headline success-rate tile counts success the same way the detail view
  // does (outcomes), and the whole row honors the top-level window (days).
  var outcomes = (state.sr.outcomes || []).join(',');
  var qs = 'outcomes=' + encodeURIComponent(outcomes) + '&days=' + encodeURIComponent(String(state.days));
  // The AI Defect Rate tile is fed by its own endpoint (DEMO MOCK — see
  // metrics/defectRate.ts), fetched here so the tile has a figure even when its
  // detail section was never opened. loadDefectRate repaints the row on arrival.
  loadDefectRate();
  get('/api/kpis?' + qs + scopeQs()).then(function (k) {
    lastKpis = k;
    paintKpis();
  });
  // The churn tile rides along, cached per scope + churn window T (it ignores
  // `days` — see loadChurnKpi).
  loadChurnKpi();
  // The Time to Ship tile is fed by its own endpoint (DEMO MOCK — see
  // metrics/timeToShip.ts), fetched here so the tile has a figure even when its
  // detail section was never opened. loadTimeToShip repaints the row on arrival.
  loadTimeToShip();
}

// The churn headline tile: the trailing AI-vs-human comparison over the last
// fully matured window. Rendered as a two-stat block — each rate a prominent
// number with a small caption beneath, split by a thin divider — so the numbers
// lead and "AI"/"human" read as labels, not part of the value. The trailing-
// window disclosure ("within Nd, through <date>") is verbose for a tile, so it
// moves to a hover tooltip rather than a two-line sub. Each rate carries its
// own small delta (vs the equally-matured prior window) beside its caption —
// one composite badge couldn't speak for both. Ignores state.days (loadChurnKpi).
// The Time to Ship headline tile — median idea→merged ship time for features in
// the window, fed by getTtsKpi() (published by the detail's fetch, or the
// background loadTimeToShip in loadKpis). DEMO MOCK — see metrics/timeToShip.ts.
// Lower is better, so the delta's "good" direction is down.
function timeToShipTile(): Tile {
  var t = getTtsKpi();
  var cur = t && t.kpi ? t.kpi.current : null;
  var prev = t && t.kpi ? t.kpi.previous : null;
  var have = cur && cur.hours != null;
  var n = (cur && cur.count) || 0;
  // V2: BOTH kinds side by side, churn-tile style — one stat per kind (median
  // ship time big, "N tickets/PRs" as the caption, hours delta beneath; lower
  // is better). Kinds arrive from two fetches, so a stat renders as soon as its
  // kind's KPI lands rather than waiting for both.
  if (t && t.v2) {
    var kinds = t.kinds || {};
    var v2stat = function (kpi, noun) {
      if (!kpi || !kpi.current || kpi.current.hours == null) return '';
      var c = kpi.current, p = kpi.previous || {};
      return '<span class="ch-stat"><span class="ch-rate">' + esc(dur(c.hours)) + '</span>' +
        '<span class="ch-cap">' + esc(num(c.count) + ' ' + noun) + '</span>' +
        kpiDelta(c.hours, p.hours, 'rel', 'down') + '</span>';
    };
    var tkStat = v2stat(kinds.ticket, 'tickets');
    var prStat = v2stat(kinds.pr, 'PRs');
    var haveV2 = !!(tkStat || prStat);
    return {
      label: 'Time to ship',
      value: haveV2
        ? '<span class="ch-duo">' + tkStat + (tkStat && prStat ? '<span class="ch-div"></span>' : '') + prStat + '</span>'
        : '—',
      valueHtml: true,
      valueClass: 'value-duo',
      delta: '',
      sub: haveV2 ? 'median ship time · shipped in window' : 'nothing shipped in window',
      title: 'Median time from first work to shipped, and how many shipped in the window, per artifact kind. Delta vs the prior same-length window; lower is better.',
      metric: 'time_to_ship'
    };
  }
  return {
    label: 'Time to ship',
    value: have ? dur(cur.hours) : '—',
    delta: have ? kpiDelta(cur.hours, prev ? prev.hours : null, 'rel', 'down') : '',
    sub: have ? n + (n === 1 ? ' feature' : ' features') : 'no features shipped in window',
    title: 'Median time from a feature\'s first work to its last PR merging, across the Idea → Coding → Pickup → Review pipeline.',
    metric: 'time_to_ship'
  };
}

function churnTile(): Tile {
  var c = lastChurn;
  var kpi = c && c.kpi ? c.kpi : { current: null };
  var cur = kpi.current || {};
  var prev = (kpi as any).previous || {};
  var have = cur.rate != null;
  // Each cohort gets its own delta vs the equally-matured prior T-day window
  // (lower churn is better → 'down' is favorable). One arrow per rate, since a
  // single badge can't speak for both; kept small + muted under the caption.
  var stat = function (rate, prevRate, label) {
    return '<span class="ch-stat"><span class="ch-rate">' + fmtVal(rate, 'pct') + '</span>' +
      '<span class="ch-cap">' + label + '</span>' + kpiDelta(rate, prevRate, 'points', 'down') + '</span>';
  };
  return {
    label: 'Code churn rate',
    value: have
      ? '<span class="ch-duo">' + stat(cur.aiRate, prev.aiRate, 'AI') + '<span class="ch-div"></span>' + stat(cur.humanRate, prev.humanRate, 'human') + '</span>'
      : '—',
    valueHtml: true,
    valueClass: 'value-duo',
    delta: '',
    // Sub stays terse; the full trailing-window context is the tooltip.
    sub: have ? 'trailing ' + c.windowDays + 'd · matured' : 'no matured attributed lines yet',
    title: have
      ? 'Share of AI- vs human-authored lines removed again within ' + c.windowDays +
        ' days of merging. Trailing figure through ' + dayOf(c.throughDate) +
        ' (only fully matured cohorts count).'
      : undefined,
    metric: 'churn'
  };
}

// Render the headline tile row from the last fetched payload + current store
// state. Separated from the fetch so an overview load can trigger a repaint (the
// "any outcomes recorded?" check below depends on it).
export function paintKpis() {
  var k = lastKpis;
  if (!k || k.error) { $('#kpis').innerHTML = ''; return; }
  var cur = k.current || {}, prev = k.previous || {};
  var cpf = cur.costPerFeature || {}, ppf = prev.costPerFeature || {};
  var cpr = cur.costPerPr || {}, ppr = prev.costPerPr || {};
  var cpt = cur.costPerTicket || {}, ppt = prev.costPerTicket || {};
  // Smart default: open on a kind that HAS data (see caKind.ts for the ordering and the
  // two rules that outrank it). An install whose only shipped artifacts are Jira tickets —
  // every dogfood install, 0 features vs 22 closed tickets — otherwise landed on an empty
  // "cost per shipped feature" tile with a populated ticket tree one toggle away.
  var defaultKind = smartDefaultKind({
    feature: cpf.count || 0,
    ticket: cpt.count || 0,
    pr: cpr.count || 0,
  });
  state.ca.defaultKind = defaultKind;
  // The tile mirrors the kind the user picked in the detail; until then, the smart
  // default. Applied ONCE per page: `defaultKind` is recomputed on every repaint and a
  // repaint follows every window and scope change, so re-deciding here moved the toggle
  // out from under a user who had scrolled to it — changing the window silently threw them
  // onto the Ticket tab.
  var res = resolveKind(state.ca, defaultKind);
  state.ca.kind = res.kind;
  state.ca.kindResolved = res.resolved;
  // A kind flip re-words every panel in the section (CA_KINDS drives the titles, the
  // legend, the notes and the axis control) and changes what its charts fetch, so a
  // section already rendered for the previous kind is now stale in both. It used to be
  // left standing: Feature titles and a Feature toggle around ticket-worded legends, over
  // ticket data — which is why switching tabs and back "fixed" it.
  //
  // Deferred, and re-checked inside: paintKpis has many callers, one of them reached FROM a
  // render (renderBurn mirrors the filtered KPI), so re-rendering synchronously could
  // re-enter the render it was called from. A tick later the flip is already recorded, so
  // the nested repaint reports no move and this fires at most once.
  if (res.changed) {
    setTimeout(function () {
      if (state.metric === 'cost_artifact') renderOpenMetric();
    }, 0);
  }
  // "closed" not "shipped" for tickets, and "Jira item" not "ticket": the count spans
  // every level (an epic and its stories all count), so this is spend per closed item,
  // not per delivered increment — see Store.costPerArtifact.
  var caData = state.ca.kind === 'pr'
    ? { cur: cpr, prev: ppr, label: 'per merged PR', noun: 'PR', nounPl: 'PRs', verb: 'merged' }
    : state.ca.kind === 'ticket'
      ? { cur: cpt, prev: ppt, label: 'per closed Jira item', noun: 'item', nounPl: 'items', verb: 'closed' }
      : { cur: cpf, prev: ppf, label: 'per shipped feature', noun: 'feature', nounPl: 'features', verb: 'shipped' };
  // When a complexity filter is active, the tile mirrors the filtered figure the
  // Cost-by-Artifact endpoint computed — but only if the override still matches the
  // current kind/window/complexity (guards against a stale filter after a switch).
  var caCx = state.ca.complexity;
  var useCaFilter = !!caCx && caKpiOverride && caKpiOverride.kind === state.ca.kind &&
    caKpiOverride.days === state.days && caKpiOverride.complexity === caCx &&
    caKpiOverride.scope === scopeQs();
  if (useCaFilter) {
    caData.cur = caKpiOverride.kpi.current || {};
    caData.prev = caKpiOverride.kpi.previous || {};
  }
  var cnt = caData.cur.count || 0;
  var caSub = useCaFilter
    ? caData.label + ' · counting ' + cnt + ' ' + (cnt === 1 ? caData.noun : caData.nounPl) + ' of complexity: ' + cxLabelList(caCx)
    : caData.label + ' · ' + cnt + ' ' + (cnt === 1 ? caData.noun : caData.nounPl) + ' ' + caData.verb;
  // A "0%" outcome rate only means something once the selected success definition
  // can actually be satisfied; when none of its outcome types exist (e.g. the
  // default `session_success` before LLM enrichment has run), the rate is a
  // structural 0, so show "—" (and no delta) rather than a misleadingly bad number.
  // successDefinable reads the overview, so this corrects once that lands (see the
  // paintKpis callers in main.ts) and the enrichment nudge explains why.
  var srKnown = successDefinable();
  // Published by the AI Defect Rate detail (or its background fetch in loadKpis),
  // so the tile mirrors whatever lookback + filters that section is showing.
  var drK = getDrKpi();
  var dr = drK ? drK.kpi : null;
  var drLookback = drK ? drK.lookback : 30;
  var tiles: Tile[] = [
    // Cost per shipped artifact leads the row (it is also the default open
    // metric — see DEFAULT_METRIC); Session Outcome Rate follows.
    { label: 'Cost per shipped artifact', value: caData.cur.costPerUnit != null ? usd(caData.cur.costPerUnit) : '—',
      delta: kpiDelta(caData.cur.costPerUnit, caData.prev.costPerUnit, 'rel', 'down'), sub: caSub,
      metric: 'cost_artifact' },
    { label: 'Session Outcome Rate', value: srKnown ? fmtVal(cur.successRate, 'pct') : '—',
      delta: srKnown ? kpiDelta(cur.successRate, prev.successRate, 'points', 'up') : '',
      sub: srKnown ? 'of sessions in window' : 'no matching outcomes yet', metric: 'success_rate' },
    // AI Defect Rate. Higher is worse, so the delta's "good" direction is
    // down, the same as cost. '—' until its endpoint answers.
    { label: 'AI defect rate', value: dr ? fmtVal(dr.current.rate, 'pct') : '—',
      delta: dr ? kpiDelta(dr.current.rate, dr.previous.rate, 'points', 'down') : '',
      sub: dr
        ? (drLookback == null
            ? 'of AI-assisted PRs with a defect at any time'
            : 'of AI-assisted PRs with a defect within ' + drLookback + 'd')
        : 'loading…',
      metric: 'defect_rate' },
    churnTile(),
    // Time to Ship — DEMO MOCK — takes the slot Total Spend held. Lower is better.
    timeToShipTile()
    // Tool error rate stays off the headline row (a plumbing measure, not an
    // outcome one). Its detail (metrics/ops.ts) is still wired into
    // renderOpenMetric, so restoring the tile is a one-line change here.
  ];
  $('#kpis').innerHTML = tiles.map(function (t) {
    // Tiles with a metric key are clickable nav into that metric's detail view.
    var cls = 'tile' + (t.metric ? ' clickable' : '') + (t.metric && t.metric === state.metric ? ' on' : '');
    var attr = t.metric ? ' data-metric="' + t.metric + '"' : '';
    var valCls = 'value' + (t.valueClass ? ' ' + t.valueClass : '');
    // Most values are plain scalars and get escaped; a tile that opts into
    // valueHtml (the churn composite) has pre-built its own safe markup.
    var valInner = t.valueHtml ? t.value : esc(t.value);
    var titleAttr = t.title ? ' title="' + esc(t.title) + '"' : '';
    return '<div class="' + cls + '"' + attr + titleAttr + '><div class="label">' + esc(t.label) + '</div><div class="' + valCls + '"><span class="num">' +
      valInner + '</span>' + (t.delta || '') + '</div><div class="sub">' + esc(t.sub) + '</div></div>';
  }).join('');
  Array.prototype.forEach.call(document.querySelectorAll('#kpis .tile[data-metric]'), function (el) {
    el.onclick = function () { clearAsked(); openMetric(el.getAttribute('data-metric')); };
  });
}

// Tiles act as nav: one section is always expanded; clicking a tile switches to it.
export function openMetric(m, force?) {
  if (state.metric === m && !force) return; // already expanded (unless forced)
  state.metric = m;
  state.view = 'dashboard';
  syncKpiActive();
  // The top selector row swaps in/out churn's own controls, so repaint it on
  // every metric switch (churn ⇄ anything else changes what that row shows).
  renderWindow();
  renderOpenMetric();
  syncHash(); // mirror the metric into the URL (no-op while a route is applying)
}

export function syncKpiActive() {
  Array.prototype.forEach.call(document.querySelectorAll('#kpis .tile[data-metric]'), function (el) {
    el.classList.toggle('on', el.getAttribute('data-metric') === state.metric);
  });
}
